import React, { useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import styles from "./CouponTab.module.css";
import axios from "axios";
import qs from "qs";
import { useDispatch } from "react-redux";
import { getPlayerBalance } from "../../store/slices/user";

// Use backend base URL from environment, fallback to production API
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.kuga59.co"
).replace(/\/+$/, "");

export default function CouponTab({ userId, onUpdated }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // 'success' | 'error' | 'info'
  const [longText, setLongText] = useState("");
  const dispatch = useDispatch();

  const resolveUserId = useCallback(() => {
    if (userId) return userId;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch (_) {}
    const idFallback = localStorage.getItem("userId");
    return idFallback || null;
  }, [userId]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!code.trim()) return;
      const uid = resolveUserId();
      if (!uid) {
        setMessage("กรุณาเข้าสู่ระบบก่อนใช้งานคูปอง");
        setMessageType("error");
        return;
      }

      setLoading(true);
      setMessage("");
      setLongText("");

      try {
        const body = qs.stringify({ code: code.trim() });
        const res = await axios.put(
          `${API_BASE_URL}/creditcode/usecode/${uid}`,
          body,
        );

        const msg = res?.data?.message;
        const ltxt = res?.data?.longtext || "";
        setLongText(ltxt);

        if (msg === "เติมเครดิตโค้ดเรียบร้อย") {
          setMessage("เติมเครดิตโค้ดเรียบร้อย");
          setMessageType("success");
          setTimeout(() => {
            try {
              dispatch(getPlayerBalance({ id: uid }));
            } catch (_) {}
            if (typeof onUpdated === "function") onUpdated();
          }, 3000);
        } else if (msg === "เครดิตโค้ดถูกใช้งานแล้ว") {
          setMessage("เครดิตโค้ดถูกใช้งานแล้ว");
          setMessageType("error");
        } else if (msg === "ไม่พบเครดิตโค้ด") {
          setMessage("ไม่พบเครดิตโค้ด");
          setMessageType("error");
        } else if (msg === "เครดิตโค้ดถูกใช้งานครบแล้ว") {
          setMessage("เครดิตโค้ดถูกใช้งานครบแล้ว");
          setMessageType("error");
        } else if (msg === "คุณได้ใช้เครดิตโค้ดนี้ไปแล้ว") {
          setMessage("คุณได้ใช้เครดิตโค้ดนี้ไปแล้ว");
          setMessageType("error");
        } else if (msg === "โปรดเคลียร์เทิร์นก่อนเติมเครดิตโค้ด") {
          setMessage("กรุณาเคลียเทิร์นก่อนรับเครดิตโค้ด");
          setMessageType("error");
        } else {
          setMessage("ไม่สามารถเติมเครดิตได้");
          setMessageType("error");
        }
        setCode("");
      } catch (err) {
        console.error("Coupon submit error", err);
        setMessage("ไม่สามารถเติมเครดิตได้");
        setMessageType("error");
      } finally {
        setLoading(false);
      }
    },
    [code, onUpdated, resolveUserId, dispatch],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <Icon icon="ri:coupon-3-fill" className={styles.icon} />
        </div>
        <h4 className={styles.title}>🎫 รับคูปองฟรี</h4>
        <p className={styles.subtitle}>กรอกรหัสคูปองเพื่อรับเครดิตฟรี</p>
      </div>

      <form className={styles.card} onSubmit={onSubmit}>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="couponCode">
            รหัสคูปอง
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="couponCode"
              className={styles.input}
              placeholder="กรอกรหัสคูปอง..."
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={loading}
            />
            <div className={styles.inputIcon}>
              <Icon icon="ri:coupon-3-fill" className={styles.ticketIcon} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className={`${styles.submit} ${loading ? styles.loading : ""} ${
            !code.trim() ? styles.disabled : ""
          }`}
          disabled={!code.trim() || loading}
        >
          <span className={styles.buttonContent}>
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                กำลังดำเนินการ...
              </>
            ) : (
              <>
                <svg
                  className={styles.buttonIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12L10 18L20 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                ยืนยันรับคูปอง
              </>
            )}
          </span>
        </button>
      </form>

      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          <div className={styles.messageIcon}>
            {messageType === "success" ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span className={styles.messageText}>{message}</span>
        </div>
      )}

      {longText && (
        <div className={styles.reward}>
          <div className={styles.rewardHeader}>
            <svg
              className={styles.rewardIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                fill="currentColor"
              />
            </svg>
            <span>รางวัลที่ได้รับ</span>
          </div>
          <div className={styles.rewardContent}>{longText}</div>
        </div>
      )}
    </div>
  );
}
